import { useQuery } from '@apollo/client/react';
import { type FC, useState } from 'react';

import {
  GetBookingDocumentsDocument,
  type GetBookingDocumentsQueryResult
} from '@zen/Booking/BookingDetails/BookingOverview/components/DocumentsContainer/graphql';
import AIListItem from '@zen/Components/AIListItem/AIListItem';
import DocumentPreviewModal from '@zen/Components/Documents/DocumentList/DocumentItem/DocumentPreviewModal';
import { Button } from '@zen/DesignSystem';
import type { BookingDocument, SuggestionSource } from '@zen/graphql/types.generated';
import type { Nullable } from '@zen/utils/typescript';

import { enumToSentenceCase, extractProposedText } from '../helpers';
import type { Suggestion } from '../types';

interface SuggestionsListProps {
  onAccept: (id: string) => void;
  onReject: (ids: string[]) => void;
  suggestions: Suggestion[];
  zencargoReference?: Nullable<string>;
}

export const SuggestionsList: FC<SuggestionsListProps> = (props) => {
  const { onAccept, onReject, suggestions, zencargoReference } = props;

  const [documentName, setDocumentName] = useState<Nullable<string>>(null);
  const [previewUrl, setPreviewUrl] = useState<Nullable<string>>(null);
  const [selectedSuggestionId, setSelectedSuggestionId] = useState<Nullable<string>>(null);

  const { data: bookingData } = useQuery<GetBookingDocumentsQueryResult>(GetBookingDocumentsDocument, {
    variables: { zencargoReference },
    skip: !zencargoReference
  });
  const bookingDocuments: BookingDocument[] = bookingData?.bookings?.nodes?.[0]?.bookingDocuments || [];

  const handleAccept = () => {
    if (selectedSuggestionId) onAccept(selectedSuggestionId);
  };

  const handleDocumentPreview = (documentId: string | undefined) => {
    if (!documentId) return;

    const document = bookingDocuments.find((doc) => doc.id === documentId);

    if (document?.assetUrl) {
      setPreviewUrl(document.assetUrl);
    }

    if (document?.description) {
      setDocumentName(document.description);
    }
  };

  const handleReject = () => {
    const suggestionIds = suggestions.map((suggestion) => {
      return suggestion.id;
    });

    onReject(suggestionIds);
  };

  const handleSuggestionSelect = (suggestionId: string) => {
    setSelectedSuggestionId(suggestionId);
  };

  const buildSources = (sources: SuggestionSource[]) => {
    return sources.map((source) => {
      return (
        <p key={source.sourceId} className="text-navy-base" onClick={() => handleDocumentPreview(source.sourceId)}>
          {enumToSentenceCase(source.source)}{' '}
        </p>
      );
    });
  };

  return (
    <>
      <DocumentPreviewModal
        fileTitle={documentName || ''}
        fileUrl={previewUrl || ''}
        isOpen={!!previewUrl}
        onClosePreview={() => setPreviewUrl(null)}
      />

      <div className="flex flex-col gap-1">
        {suggestions.map((suggestion) => {
          const proposedValueText = extractProposedText(suggestion.proposedValue as string, suggestion.targetAttribute);

          return (
            <AIListItem
              key={suggestion.id}
              customContent={buildSources(suggestion.sources as SuggestionSource[])}
              isSelected={suggestion.id === selectedSuggestionId}
              item={{ id: suggestion.id, label: proposedValueText }}
              onClick={() => {
                handleSuggestionSelect(suggestion.id);
              }}
            />
          );
        })}
      </div>

      <div className="flex justify-end mt-4 gap-2">
        <Button variant="ghost" onClick={handleReject}>
          Dismiss all
        </Button>
        <Button variant="primary" disabled={!selectedSuggestionId} onClick={handleAccept}>
          Apply selected value
        </Button>
      </div>
    </>
  );
};
